import { useState, useEffect, useCallback } from 'react'
import { History, Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { agentService, type AgentChangeHistoryItem } from '@/services/agent'

interface AgentHistoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  agentId: string
  agentName: string
}

// Field name translations for display
const FIELD_LABELS: Record<string, string> = {
  name: 'Nome',
  description: 'Descricao',
  system_prompt: 'Prompt do Sistema',
  is_active: 'Status',
  is_template: 'Template',
  'llm_config.provider': 'Provedor LLM',
  'llm_config.model': 'Modelo',
  'llm_config.temperature': 'Temperatura',
  'llm_config.max_tokens': 'Max Tokens',
  'memory_config.type': 'Tipo de Memoria',
  'memory_config.max_messages': 'Max Mensagens',
  'settings.timeout_seconds': 'Timeout',
  'settings.retry_attempts': 'Tentativas',
  'settings.enable_logging': 'Logging',
}

function getFieldLabel(fieldName: string): string {
  return FIELD_LABELS[fieldName] || fieldName
}

function formatValue(value: string | null, fieldName: string): string {
  if (value === null || value === '') {
    return '(vazio)'
  }

  // Handle boolean values
  if (fieldName === 'is_active' || fieldName === 'is_template' || fieldName.includes('enable_')) {
    return value === 'true' ? 'Sim' : 'Nao'
  }

  // Truncate long values
  if (value.length > 100) {
    return value.substring(0, 100) + '...'
  }

  return value
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function AgentHistoryDialog({
  open,
  onOpenChange,
  projectId,
  agentId,
  agentName,
}: AgentHistoryDialogProps) {
  const [history, setHistory] = useState<AgentChangeHistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)

  const pageSize = 10

  const fetchHistory = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await agentService.getAgentHistory(projectId, agentId, {
        page,
        page_size: pageSize,
      })
      setHistory(response.history)
      setTotalPages(response.total_pages)
      setTotal(response.total)
    } catch (err) {
      console.error('Failed to fetch agent history:', err)
      setError('Erro ao carregar historico')
    } finally {
      setIsLoading(false)
    }
  }, [projectId, agentId, page, pageSize])

  useEffect(() => {
    if (open && projectId && agentId) {
      fetchHistory()
    }
  }, [open, projectId, agentId, fetchHistory])

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1)
    }
  }

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-gray-700/50 sm:max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <History className="w-5 h-5 text-indigo-400" />
            Historico de Alteracoes
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Alteracoes realizadas no agente &quot;{agentName}&quot;
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-400">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchHistory}
                className="mt-4 border-gray-700/50 text-gray-300"
              >
                Tentar novamente
              </Button>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Nenhuma alteração registrada</p>
              <p className="text-gray-500 text-sm mt-1">
                As alterações feitas no agente serão exibidas aqui.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="bg-white/5 rounded-lg p-4 border border-gray-700/30"
                >
                  <div className="flex items-start justify-between mb-2">
                    <Badge
                      variant="outline"
                      className="border-indigo-500/50 text-indigo-300"
                    >
                      {getFieldLabel(item.field_name)}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      {formatDate(item.created_at)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Valor anterior</p>
                      <p className="text-sm text-gray-400 bg-red-500/10 rounded px-2 py-1 font-mono">
                        {formatValue(item.old_value, item.field_name)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Novo valor</p>
                      <p className="text-sm text-gray-300 bg-green-500/10 rounded px-2 py-1 font-mono">
                        {formatValue(item.new_value, item.field_name)}
                      </p>
                    </div>
                  </div>

                  {(item.changed_by_name || item.changed_by_email) && (
                    <p className="text-xs text-gray-500 mt-3">
                      Por:{' '}
                      <span className="text-gray-400">
                        {item.changed_by_name || item.changed_by_email}
                      </span>
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {!isLoading && !error && history.length > 0 && (
          <div className="flex items-center justify-between pt-4 border-t border-gray-700/30 mt-4">
            <p className="text-sm text-gray-500">
              {total} {total === 1 ? 'alteração encontrada' : 'alterações encontradas'}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handlePreviousPage}
                disabled={page === 1}
                className="w-8 h-8 border-gray-700/50 text-gray-400"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-400">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={handleNextPage}
                disabled={page === totalPages}
                className="w-8 h-8 border-gray-700/50 text-gray-400"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
